import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Users, Trophy, ListOrdered, GitBranch, Plus, Trash2,
  Upload, Sparkles, X, Share2, Check, Zap, Activity, Megaphone, UserCog, ImagePlus, Loader2, Inbox, Clock, MapPin, Settings
} from 'lucide-react';
import { api, apiError } from './api';
import OfficialsTab, { AssignModal } from './OfficialsTab';
import OperationsTab from './OperationsTab';
import CommsTab from './CommsTab';
import { coverSrc, Crest } from './Tournaments';
import { Celebration, computeChampion } from './Celebration';
import SettingsModal from './TournamentSettings';

const TABS = [
  { id: 'teams', label: 'Table', icon: <Users size={16} /> },
  { id: 'entries', label: 'Entries', icon: <Inbox size={16} /> },
  { id: 'fixtures', label: 'Fixtures', icon: <Trophy size={16} /> },
  { id: 'standings', label: 'Standings', icon: <ListOrdered size={16} /> },
  { id: 'bracket', label: 'Knockouts', icon: <GitBranch size={16} /> },
  { id: 'officials', label: 'Officials', icon: <UserCog size={16} /> },
  { id: 'operations', label: 'Ops Centre', icon: <Activity size={16} /> },
  { id: 'comms', label: 'Comms', icon: <Megaphone size={16} /> },
];

export default function TournamentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [standings, setStandings] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [tab, setTab] = useState('teams');
  const [copied, setCopied] = useState(false);
  const [champDismissed, setChampDismissed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const teamMap = useMemo(() => Object.fromEntries(teams.map((x) => [x.id, x])), [teams]);
  const teamName = (tid) => (tid ? (teamMap[tid]?.name || '—') : 'TBD');
  const champion = useMemo(() => (t ? computeChampion(fixtures, t, teamMap) : null), [fixtures, t, teamMap]);
  const champKey = champion ? `rc-champ-${id}-${champion.id}` : null;
  const showChamp = !!champion && !champDismissed && !(champKey && typeof localStorage !== 'undefined' && localStorage.getItem(champKey));

  const loadAll = async () => {
    try {
      const [tr, te, fx, st, of] = await Promise.all([
        api.get(`/tournaments/${id}`), api.get(`/tournaments/${id}/teams`),
        api.get(`/tournaments/${id}/fixtures`), api.get(`/tournaments/${id}/standings`),
        api.get(`/tournaments/${id}/officials`),
      ]);
      setT(tr.data); setTeams(te.data); setFixtures(fx.data); setStandings(st.data); setOfficials(of.data);
    } catch (e) { if (e?.response?.status === 401) nav('/login'); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const share = async () => {
    const url = `${window.location.origin}/live/${id}`;
    try {
      if (navigator.share) await navigator.share({ title: t?.name, url });
      else { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    } catch {}
  };

  if (!t) return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center">Loading…</div>;

  return (
    <div className="ttheme min-h-screen bg-[#0A0A0A] text-white font-sans pb-12">
      {showChamp && <Celebration champion={champion} onClose={() => { if (champKey) localStorage.setItem(champKey, '1'); setChampDismissed(true); }} />}
      {showSettings && <SettingsModal t={t} onClose={() => setShowSettings(false)} onSaved={loadAll} />}
      <div className="max-w-3xl mx-auto px-5">
        {t.cover_photo && (
          <div className="mt-5 rounded-2xl overflow-hidden border border-gray-800 h-40 sm:h-48 relative" data-testid="detail-cover">
            <img src={coverSrc(t.cover_photo)} alt={t.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </div>
        )}
        <div className="py-5 border-b border-gray-800">
          <Link to="/tournaments" className="text-gray-500 text-sm flex items-center gap-1 mb-3"><ArrowLeft size={15}/> All tournaments</Link>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-black tracking-wide">{t.name}</h1>
              <p className="text-xs text-gray-500 mt-1 uppercase tracking-widest">{t.format.replace('_', ' + ')} • {teams.length} teams</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="tournament-settings-btn" onClick={() => setShowSettings(true)} title="Tournament settings" className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-3 py-2 text-xs font-bold flex items-center gap-1.5">
                <Settings size={14} />
              </button>
              <button data-testid="share-tournament" onClick={share} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-3 py-2 text-xs font-bold flex items-center gap-1.5">
                {copied ? <><Check size={14} className="text-[#00D26A]"/> Copied</> : <><Share2 size={14}/> Public</>}
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4 overflow-x-auto pb-2 -mx-1 px-1">
          {TABS.map((tb) => (
            <button key={tb.id} data-testid={`tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 ${tab === tb.id ? 'bg-[#00BFFF] text-black' : 'bg-[#1C1C1E] text-gray-400 border border-gray-800'}`}>
              {tb.icon} {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'teams' && <TeamsTab id={id} t={t} teams={teams} reload={loadAll} />}
          {tab === 'entries' && <EntriesTab id={id} t={t} reload={loadAll} />}
          {tab === 'fixtures' && <FixturesTab id={id} tName={t?.name} teams={teams} fixtures={fixtures} teamName={teamName} teamObj={(tid) => teamMap[tid]} officials={officials} reload={loadAll} />}
          {tab === 'standings' && <StandingsTab standings={standings} />}
          {tab === 'bracket' && <BracketTab fixtures={fixtures} teamName={teamName} teamObj={(tid) => teamMap[tid]} />}
          {tab === 'officials' && <OfficialsTab tid={id} tName={t?.name} />}
          {tab === 'operations' && <OperationsTab tid={id} />}
          {tab === 'comms' && <CommsTab tid={id} />}
        </div>
      </div>
    </div>
  );
}

function TeamsTab({ id, t, teams, reload }) {
  const [name, setName] = useState('');
  const [manager, setManager] = useState('');
  const [logo, setLogo] = useState('');
  const [csv, setCsv] = useState('');
  const [showCsv, setShowCsv] = useState(false);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [logoBusy, setLogoBusy] = useState(null);
  const [groupPrompt, setGroupPrompt] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [groupCountInput, setGroupCountInput] = useState(2);
  const [doubleRR, setDoubleRR] = useState(false);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data.url;
  };

  const onNewLogo = async (file) => {
    if (!file) return;
    setErr(''); setUploading(true);
    try { setLogo(await uploadImage(file)); } catch (e) { setErr(apiError(e)); }
    setUploading(false);
  };

  const onRowLogo = async (tid, file) => {
    if (!file) return;
    setErr(''); setLogoBusy(tid);
    try {
      const url = await uploadImage(file);
      await api.put(`/teams/${tid}/logo`, { logo: url });
      reload();
    } catch (e) { setErr(apiError(e)); }
    setLogoBusy(null);
  };

  const add = async () => {
    if (!name.trim()) return;
    setErr('');
    try { await api.post(`/tournaments/${id}/teams`, { name, manager, logo }); setName(''); setManager(''); setLogo(''); reload(); }
    catch (e) { setErr(apiError(e)); }
  };
  const del = async (tid) => { await api.delete(`/teams/${tid}`); reload(); };
  const importCsv = async () => {
    setErr('');
    try { await api.post(`/tournaments/${id}/teams/import`, { csv_text: csv }); setCsv(''); setShowCsv(false); reload(); }
    catch (e) { setErr(apiError(e)); }
  };
  const runGenerate = async (opts) => {
    setErr(''); setGenBusy(true);
    try {
      await api.post(`/tournaments/${id}/fixtures/generate`, opts || {});
      setGroupPrompt(false);
      reload();
    } catch (e) { setErr(apiError(e)); }
    setGenBusy(false);
  };
  const generate = async () => {
    setErr('');
    if (t?.format === 'groups_knockout' || t?.format === 'league') {
      setGroupCountInput(autoGroupCount); setDoubleRR(false); setGroupPrompt(true); return;
    }
    runGenerate();
  };
  const pitchCount = (t?.pitches || []).filter(Boolean).length;
  const maxGroups = Math.max(1, Math.floor(teams.length / 2));
  const autoGroupCount = Math.max(2, Math.min(pitchCount >= 2 ? pitchCount : Math.ceil(teams.length / 4), maxGroups || 1));
  const inputCls = "flex-1 bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 text-white text-sm focus:outline-none focus:border-[#00BFFF]";

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-stretch">
        <label data-testid="new-team-logo-label" className="shrink-0 w-12 bg-[#1C1C1E] border border-gray-800 rounded-xl flex items-center justify-center cursor-pointer hover:border-[#00BFFF]/60">
          <input type="file" accept="image/*" data-testid="new-team-logo-input" className="hidden" onChange={(e) => onNewLogo(e.target.files?.[0])} />
          {uploading ? <Loader2 size={16} className="animate-spin text-gray-400" /> : logo ? <img src={coverSrc(logo)} alt="logo" className="w-8 h-8 rounded-full object-cover" /> : <ImagePlus size={16} className="text-gray-500" />}
        </label>
        <input data-testid="team-name-input" className={inputCls} placeholder="Team name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input className="w-28 bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 text-white text-sm focus:outline-none focus:border-[#00BFFF]" placeholder="Manager" value={manager} onChange={(e) => setManager(e.target.value)} />
        <button data-testid="add-team-btn" onClick={add} className="bg-[#00BFFF] text-black font-bold px-4 rounded-xl"><Plus size={18}/></button>
      </div>
      <div className="flex gap-2">
        <button data-testid="csv-toggle" onClick={() => setShowCsv(!showCsv)} className="flex-1 bg-[#1C1C1E] border border-gray-800 rounded-xl py-2.5 text-xs font-bold text-gray-300 flex items-center justify-center gap-2"><Upload size={14}/> CSV IMPORT</button>
        <button data-testid="generate-fixtures-btn" onClick={generate} className="flex-1 bg-[#1C1C1E] border border-[#00BFFF]/40 text-[#00BFFF] rounded-xl py-2.5 text-xs font-bold flex items-center justify-center gap-2"><Zap size={14}/> GENERATE FIXTURES</button>
      </div>
      {showCsv && (
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 space-y-2">
          <p className="text-[11px] text-gray-500">Paste CSV with a <code className="text-[#00BFFF]">name</code> column (optional: manager, contact, group, age_group)</p>
          <textarea data-testid="csv-textarea" className="w-full h-24 bg-[#0A0A0A] border border-gray-800 rounded-lg p-2 text-xs text-white" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={'name,manager\nLions,John\nTigers,Mary'} />
          <button data-testid="csv-import-btn" onClick={importCsv} className="w-full bg-[#00BFFF] text-black font-bold py-2 rounded-lg text-xs">IMPORT</button>
        </div>
      )}
      {err && <p data-testid="teams-error" className="text-[#FF3B30] text-sm">{err}</p>}

      {groupPrompt && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" data-testid="group-prompt">
          <div className="bg-[#121212] border border-[#00BFFF]/40 rounded-3xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#00BFFF]/15 border-2 border-[#00BFFF] flex items-center justify-center mx-auto mb-4"><GitBranch size={26} className="text-[#00BFFF]" /></div>
            <h2 className="text-lg font-black">{t?.format === 'groups_knockout' ? 'Group & fixture options' : 'Fixture format'}</h2>
            <p className="text-sm text-gray-400 mt-2">
              You have <b className="text-white">{teams.length} teams</b>{pitchCount > 0 ? <> across <b className="text-white">{pitchCount} pitch{pitchCount > 1 ? 'es' : ''}</b></> : ''}.
            </p>

            {t?.format === 'groups_knockout' && (
              <>
                <div className="flex items-center justify-center gap-4 mt-5 mb-1">
                  <button data-testid="group-count-minus" onClick={() => setGroupCountInput((n) => Math.max(1, n - 1))} disabled={genBusy}
                    className="w-10 h-10 rounded-full bg-[#1C1C1E] border border-gray-700 text-white text-xl font-bold flex items-center justify-center disabled:opacity-50">−</button>
                  <div className="text-center min-w-[88px]">
                    <div data-testid="group-count-value" className="text-4xl font-black text-[#00BFFF] leading-none">{groupCountInput}</div>
                    <div className="text-[11px] text-gray-500 mt-1">group{groupCountInput > 1 ? 's' : ''}</div>
                  </div>
                  <button data-testid="group-count-plus" onClick={() => setGroupCountInput((n) => Math.min(maxGroups, n + 1))} disabled={genBusy}
                    className="w-10 h-10 rounded-full bg-[#1C1C1E] border border-gray-700 text-white text-xl font-bold flex items-center justify-center disabled:opacity-50">+</button>
                </div>
                <p className="text-[11px] text-gray-600 mb-4">Suggested {autoGroupCount} to suit your pitches · max {maxGroups}</p>
              </>
            )}

            <p className="text-[11px] uppercase tracking-widest text-gray-500 text-left mb-2 mt-1">Fixture style</p>
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button data-testid="fixture-single" onClick={() => setDoubleRR(false)} disabled={genBusy}
                className={`rounded-xl p-3 text-left border transition-colors ${!doubleRR ? 'bg-[#00BFFF]/15 border-[#00BFFF]' : 'bg-[#1C1C1E] border-gray-700'}`}>
                <p className="text-sm font-bold text-white">Single</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Each team plays once</p>
              </button>
              <button data-testid="fixture-home-away" onClick={() => setDoubleRR(true)} disabled={genBusy}
                className={`rounded-xl p-3 text-left border transition-colors ${doubleRR ? 'bg-[#00BFFF]/15 border-[#00BFFF]' : 'bg-[#1C1C1E] border-gray-700'}`}>
                <p className="text-sm font-bold text-white">Home &amp; Away</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Each team plays twice</p>
              </button>
            </div>

            <button data-testid="group-prompt-yes" onClick={() => runGenerate({ ...(t?.format === 'groups_knockout' ? { group_count: groupCountInput } : {}), double_round_robin: doubleRR })} disabled={genBusy}
              className="w-full bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
              {genBusy ? <Loader2 size={16} className="animate-spin" /> : null}Generate fixtures
            </button>
            <button data-testid="group-prompt-cancel" onClick={() => setGroupPrompt(false)} disabled={genBusy} className="w-full text-gray-500 text-sm py-2 mt-2">Cancel</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {teams.length === 0 ? <p className="text-center text-gray-600 py-8 text-sm">No teams yet.</p> :
          teams.map((tm) => (
            <div key={tm.id} data-testid={`team-row-${tm.id}`} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <label data-testid={`team-logo-label-${tm.id}`} className="relative cursor-pointer group">
                  <input type="file" accept="image/*" data-testid={`team-logo-input-${tm.id}`} className="hidden" onChange={(e) => onRowLogo(tm.id, e.target.files?.[0])} />
                  {logoBusy === tm.id ? <span className="w-9 h-9 rounded-full bg-[#0A0A0A] border border-gray-700 flex items-center justify-center"><Loader2 size={15} className="animate-spin text-gray-400" /></span> : <Crest name={tm.name} logo={tm.logo} size={36} />}
                  <span className="absolute -bottom-1 -right-1 bg-[#00BFFF] text-black rounded-full p-0.5 opacity-90"><ImagePlus size={9} /></span>
                </label>
                <div>
                  <p className="font-bold">{tm.name}</p>
                  {tm.manager && <p className="text-xs text-gray-500">{tm.manager}{tm.group ? ` • Group ${tm.group}` : ''}</p>}
                </div>
              </div>
              <button onClick={() => del(tm.id)} className="text-gray-600 hover:text-[#FF3B30]"><Trash2 size={16}/></button>
            </div>
          ))}
      </div>
    </div>
  );
}

function FixturesTab({ id, tName, teams, fixtures, teamName, teamObj, officials, reload }) {
  const [active, setActive] = useState(null);
  const [assignFx, setAssignFx] = useState(null);
  const [delayBusy, setDelayBusy] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const nav = useNavigate();
  const oOf = (tid) => teamObj ? teamObj(tid) : null;
  const offName = (oid) => (officials || []).find((o) => o.id === oid)?.name;
  const delay = async (mins) => {
    setDelayBusy(true);
    try { await api.post(`/tournaments/${id}/delay`, { minutes: mins }); await reload(); setNotifyOpen(true); } catch {}
    setDelayBusy(false);
  };
  const teamFixtures = (team) => [...fixtures]
    .filter((f) => (f.home_team_id === team.id || f.away_team_id === team.id) && f.status !== 'completed' && f.kickoff)
    .sort((a, b) => (a.kickoff || '').localeCompare(b.kickoff || ''));
  const teamMsg = (team) => {
    const lines = [`Hi ${team.manager || team.name}, updated kick-off times for ${team.name}${tName ? ` at ${tName}` : ''}:`, ''];
    teamFixtures(team).forEach((f) => {
      const opp = f.home_team_id === team.id ? teamName(f.away_team_id) : teamName(f.home_team_id);
      lines.push(`${f.kickoff}${f.pitch ? ` · ${f.pitch}` : ''} — v ${opp}`);
    });
    lines.push('', `Live: ${window.location.origin}/live/${id}`);
    return encodeURIComponent(lines.join('\n'));
  };
  const waManager = (team) => { const d = (team.contact || '').replace(/\D/g, ''); return d.length >= 7 ? `https://wa.me/${d}?text=${teamMsg(team)}` : `https://wa.me/?text=${teamMsg(team)}`; };
  const teamsToNotify = (teams || []).filter((tm) => teamFixtures(tm).length > 0);
  if (fixtures.length === 0) return <p className="text-center text-gray-600 py-10 text-sm">No fixtures yet. Add teams and tap Generate Fixtures.</p>;
  const rounds = {};
  fixtures.forEach((f) => { const k = `${f.stage}-${f.round}${f.group ? '-' + f.group : ''}`; (rounds[k] = rounds[k] || []).push(f); });
  const hasSchedule = fixtures.some((f) => f.kickoff);

  return (
    <div className="space-y-5">
      {hasSchedule && (
        <div data-testid="delay-bar" className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider flex items-center gap-1.5"><Clock size={13} /> Running late? Bump remaining kick-offs</span>
          <div className="flex items-center gap-1.5">
            {[5, 10, 15].map((m) => (
              <button key={m} type="button" data-testid={`delay-plus-${m}`} disabled={delayBusy} onClick={() => delay(m)}
                className="bg-[#0A0A0A] border border-[#00BFFF]/40 text-[#00BFFF] text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-40">+{m}m</button>
            ))}
            <button type="button" data-testid="delay-minus-5" disabled={delayBusy} onClick={() => delay(-5)}
              className="bg-[#0A0A0A] border border-gray-700 text-gray-400 text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-40">-5m</button>
            <button type="button" data-testid="notify-managers-btn" onClick={() => setNotifyOpen(true)}
              className="bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1"><Megaphone size={12} /> Notify</button>
          </div>
        </div>
      )}
      {Object.entries(rounds).map(([k, fxs]) => (
        <div key={k}>
          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-2">{fxs[0].group ? `Group ${fxs[0].group} • ` : ''}{fxs[0].stage} round {fxs[0].round}</p>
          <div className="space-y-2">
            {fxs.map((f) => (
              <div key={f.id} data-testid={`fixture-${f.id}`} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3">
                {(f.kickoff || f.pitch || f.assignments?.referee) && (
                  <div data-testid={`fixture-meta-${f.id}`} className="flex items-center gap-3 mb-2 text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                    {f.kickoff && <span className="flex items-center gap-1"><Clock size={11} />{f.kickoff}</span>}
                    {f.pitch && <span className="flex items-center gap-1"><MapPin size={11} />{f.pitch}</span>}
                    {f.assignments?.referee && <span className="flex items-center gap-1"><UserCog size={11} />{offName(f.assignments.referee) || 'Ref'}</span>}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm flex-1 truncate flex items-center gap-2"><Crest name={teamName(f.home_team_id)} logo={oOf(f.home_team_id)?.logo} size={24} />{teamName(f.home_team_id)}</span>
                  {f.status === 'completed'
                    ? <span className="flex items-center gap-1.5"><span className="text-[8px] font-black text-[#00D26A] border border-[#00D26A]/50 rounded px-1 py-0.5 uppercase tracking-wider">FT</span><span className="font-black bg-black px-3 py-1 rounded-lg text-sm">{f.home_score} - {f.away_score}</span></span>
                    : <span className="text-gray-600 text-xs px-3">vs</span>}
                  <span className="font-bold text-sm flex-1 truncate text-right flex items-center justify-end gap-2">{teamName(f.away_team_id)}<Crest name={teamName(f.away_team_id)} logo={oOf(f.away_team_id)?.logo} size={24} /></span>
                </div>
                <div className="flex justify-end gap-3 mt-2 items-center">
                  {f.status === 'completed' && f.summary && <span className="text-[10px] text-gray-500 italic flex-1 truncate">{f.summary.slice(0, 60)}…</span>}
                  {f.assignments?.referee && <span className="text-[10px] text-gray-500 mr-auto">Ref assigned ✓</span>}
                  <button data-testid={`assign-${f.id}`} onClick={() => setAssignFx(f)} className="text-[11px] font-bold text-gray-400 hover:text-white">Officials</button>
                  <button data-testid={`referee-live-${f.id}`} onClick={() => nav(`/referee/${f.id}`)} disabled={!f.home_team_id || !f.away_team_id}
                    className="text-[11px] font-bold text-[#00D26A] disabled:text-gray-700 flex items-center gap-1">
                    <Zap size={12} /> Referee Live
                  </button>
                  <button data-testid={`enter-result-${f.id}`} onClick={() => setActive(f)} disabled={!f.home_team_id || !f.away_team_id}
                    className="text-[11px] font-bold text-[#00BFFF] disabled:text-gray-700">
                    {f.status === 'completed' ? 'Edit / Report' : 'Quick Result'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {notifyOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4" data-testid="notify-modal">
          <div className="bg-[#121212] border border-gray-800 rounded-3xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold flex items-center gap-2"><Megaphone size={18} /> Notify managers</h2>
              <button data-testid="notify-close" onClick={() => setNotifyOpen(false)} className="text-gray-500"><X size={22} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">Send each manager their team's updated kick-off times on WhatsApp. Tap a team to open a pre-filled message{teamsToNotify.some((tm) => !(tm.contact || '').replace(/\D/g, '')) ? ' (teams without a saved number open WhatsApp share so you can pick the chat)' : ''}.</p>
            {teamsToNotify.length === 0 ? (
              <p className="text-center text-gray-600 py-6 text-sm">No upcoming fixtures to notify about.</p>
            ) : (
              <div className="space-y-2">
                {teamsToNotify.map((tm) => (
                  <a key={tm.id} data-testid={`notify-team-${tm.id}`} href={waManager(tm)} target="_blank" rel="noreferrer"
                    className="flex items-center justify-between gap-2 bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3 hover:border-[#25D366]/50 transition-colors">
                    <div className="min-w-0">
                      <p className="font-bold text-sm truncate flex items-center gap-2"><Crest name={tm.name} logo={tm.logo} size={22} />{tm.name}</p>
                      <p className="text-[11px] text-gray-500 truncate">{tm.manager || 'Manager'}{tm.contact ? ` · ${tm.contact}` : ' · no number saved'}</p>
                    </div>
                    <span className="shrink-0 bg-[#25D366] text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5"><Share2 size={14} /> WhatsApp</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {active && <ResultModal fixture={active} teamName={teamName} onClose={() => setActive(null)} onSaved={() => { setActive(null); reload(); }} />}
      {assignFx && <AssignModal fixture={assignFx} officials={officials} onClose={() => setAssignFx(null)} onSaved={() => { setAssignFx(null); reload(); }} />}
    </div>
  );
}

function ResultModal({ fixture, teamName, onClose, onSaved }) {
  const [hs, setHs] = useState(fixture.home_score ?? 0);
  const [as_, setAs] = useState(fixture.away_score ?? 0);
  const [events, setEvents] = useState(fixture.events || []);
  const [summary, setSummary] = useState(fixture.summary || '');
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const addEvent = (type) => setEvents([...events, { id: Date.now(), type, team: 'home', player: '', minute: '' }]);
  const updEvent = (i, k, v) => setEvents(events.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const delEvent = (i) => setEvents(events.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try { await api.post(`/fixtures/${fixture.id}/result`, { home_score: Number(hs), away_score: Number(as_), events }); onSaved(); }
    catch { setBusy(false); }
  };
  const genAI = async () => {
    setAiBusy(true);
    try {
      await api.post(`/fixtures/${fixture.id}/result`, { home_score: Number(hs), away_score: Number(as_), events });
      const { data } = await api.post(`/fixtures/${fixture.id}/ai-summary`);
      setSummary(data.summary);
    } catch {}
    setAiBusy(false);
  };
  const Step = ({ v, set }) => (
    <div className="flex items-center gap-3 bg-[#1C1C1E] border border-gray-800 rounded-xl p-2">
      <button onClick={() => set(Math.max(0, v - 1))} className="text-[#00BFFF] font-black w-8 text-xl">-</button>
      <span className="text-2xl font-black w-8 text-center">{v}</span>
      <button onClick={() => set(v + 1)} className="text-[#00BFFF] font-black w-8 text-xl">+</button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-[#121212] border border-gray-800 rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold">Match Result</h2>
          <button data-testid="result-close" onClick={onClose} className="text-gray-500"><X size={22}/></button>
        </div>
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex-1 text-center"><p className="text-xs text-gray-400 mb-2 truncate">{teamName(fixture.home_team_id)}</p><div className="flex justify-center"><Step v={hs} set={setHs}/></div></div>
          <span className="text-gray-600 font-black">-</span>
          <div className="flex-1 text-center"><p className="text-xs text-gray-400 mb-2 truncate">{teamName(fixture.away_team_id)}</p><div className="flex justify-center"><Step v={as_} set={setAs}/></div></div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex gap-2">
            <button onClick={() => addEvent('goal')} className="flex-1 bg-[#1C1C1E] border border-gray-800 rounded-lg py-2 text-[11px] font-bold">+ Goal</button>
            <button onClick={() => addEvent('yellow')} className="flex-1 bg-[#1C1C1E] border border-gray-800 rounded-lg py-2 text-[11px] font-bold">+ Yellow</button>
            <button onClick={() => addEvent('red')} className="flex-1 bg-[#1C1C1E] border border-gray-800 rounded-lg py-2 text-[11px] font-bold">+ Red</button>
          </div>
          {events.map((e, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <span className="text-[10px] uppercase text-gray-500 w-12">{e.type}</span>
              <select value={e.team} onChange={(ev) => updEvent(i, 'team', ev.target.value)} className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-1.5 text-xs">
                <option value="home">H</option><option value="away">A</option>
              </select>
              <input value={e.player} onChange={(ev) => updEvent(i, 'player', ev.target.value)} placeholder="#" className="w-12 bg-[#1C1C1E] border border-gray-800 rounded-lg p-1.5 text-xs" />
              <input value={e.minute} onChange={(ev) => updEvent(i, 'minute', ev.target.value)} placeholder="min" className="w-14 bg-[#1C1C1E] border border-gray-800 rounded-lg p-1.5 text-xs" />
              <button onClick={() => delEvent(i)} className="text-gray-600"><X size={14}/></button>
            </div>
          ))}
        </div>

        {summary && <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-3 mb-4 text-sm text-gray-300 italic">{summary}</div>}

        <button data-testid="ai-summary-btn" onClick={genAI} disabled={aiBusy} className="w-full mb-3 bg-[#1C1C1E] border border-[#00BFFF]/40 text-[#00BFFF] font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
          <Sparkles size={16}/> {aiBusy ? 'Generating…' : 'AI MATCH REPORT'}
        </button>
        <button data-testid="result-save" onClick={save} disabled={busy} className="w-full bg-[#00BFFF] text-black font-bold py-4 rounded-xl disabled:opacity-50">
          {busy ? 'Saving…' : 'SAVE RESULT'}
        </button>
      </div>
    </div>
  );
}

function StandingsTab({ standings }) {
  if (standings.length === 0) return <p className="text-center text-gray-600 py-10 text-sm">No standings yet.</p>;
  return (
    <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl overflow-hidden">
      <div className="grid grid-cols-[1.4rem_1fr_repeat(6,1.6rem)_2rem] gap-1 px-3 py-2.5 text-[10px] text-gray-500 font-bold uppercase border-b border-gray-800">
        <span>#</span><span>Team</span><span className="text-center">P</span><span className="text-center">W</span><span className="text-center">D</span><span className="text-center">L</span><span className="text-center">GF</span><span className="text-center">GA</span><span className="text-center">Pts</span>
      </div>
      {standings.map((r, i) => (
        <div key={r.team_id} data-testid={`standing-${r.team_id}`} className="grid grid-cols-[1.4rem_1fr_repeat(6,1.6rem)_2rem] gap-1 px-3 py-3 text-sm border-b border-gray-800/50 last:border-0 items-center">
          <span className="text-gray-500 text-xs">{i + 1}</span>
          <span className="font-bold truncate flex items-center gap-2"><Crest name={r.name} logo={r.logo} size={22} />{r.name}</span>
          <span className="text-center text-gray-400 text-xs">{r.P}</span>
          <span className="text-center text-gray-400 text-xs">{r.W}</span>
          <span className="text-center text-gray-400 text-xs">{r.D}</span>
          <span className="text-center text-gray-400 text-xs">{r.L}</span>
          <span className="text-center text-gray-400 text-xs">{r.GF}</span>
          <span className="text-center text-gray-400 text-xs">{r.GA}</span>
          <span className="text-center font-black text-[#00BFFF]">{r.Pts}</span>
        </div>
      ))}
    </div>
  );
}

function BracketTab({ fixtures, teamName, teamObj }) {
  const oOf = (tid) => teamObj ? teamObj(tid) : null;
  const ko = fixtures.filter((f) => f.stage === 'knockout');
  if (ko.length === 0) return <p className="text-center text-gray-600 py-10 text-sm">No knockouts yet. Use a Knockout or Groups+Knockout format and generate fixtures.</p>;
  const groups = {};
  ko.forEach((f) => {
    const key = f.round_name || `Round ${f.round}`;
    if (!groups[key]) groups[key] = { order: f.round_order || f.round, items: [] };
    groups[key].items.push(f);
  });
  const sections = Object.entries(groups).sort((a, b) => a[1].order - b[1].order);
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {sections.map(([name, sec]) => (
        <div key={name} className="min-w-[200px] flex flex-col justify-around gap-3">
          <p className="text-[11px] text-[#00BFFF] uppercase font-black tracking-widest text-center mb-1">{name}</p>
          {sec.items.map((f) => (
            <div key={f.id} className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 text-xs">
              <div className="flex justify-between"><span className="truncate flex items-center gap-1.5">{f.home_team_id ? <><Crest name={teamName(f.home_team_id)} logo={oOf(f.home_team_id)?.logo} size={18} />{teamName(f.home_team_id)}</> : <span className="text-gray-500 italic">{f.home_placeholder || 'TBD'}</span>}</span><span className="font-bold ml-2">{f.home_score ?? ''}</span></div>
              <div className="h-px bg-gray-800 my-1.5" />
              <div className="flex justify-between"><span className="truncate flex items-center gap-1.5">{f.away_team_id ? <><Crest name={teamName(f.away_team_id)} logo={oOf(f.away_team_id)?.logo} size={18} />{teamName(f.away_team_id)}</> : <span className="text-gray-500 italic">{f.away_placeholder || 'TBD'}</span>}</span><span className="font-bold ml-2">{f.away_score ?? ''}</span></div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


function EntriesTab({ id, t, reload }) {
  const [entries, setEntries] = useState([]);
  const [copied, setCopied] = useState(false);

  const load = () => api.get(`/tournaments/${id}/entries`).then((r) => setEntries(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const confirmEntry = async (eid) => { await api.post(`/entries/${eid}/confirm`); load(); if (reload) reload(); };
  const removeEntry = async (eid) => { await api.delete(`/entries/${eid}`); load(); if (reload) reload(); };

  const pending = entries.filter((e) => e.status !== 'paid');
  const paid = entries.filter((e) => e.status === 'paid');
  const publicUrl = `${window.location.origin}/live/${id}`;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="space-y-5">
      <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold flex items-center gap-2">Public entries: <span className={t?.entry_open ? 'text-[#00D26A]' : 'text-gray-500'}>{t?.entry_open ? 'Open' : 'Closed'}</span></p>
          <p className="text-xs text-gray-500 mt-0.5">{t?.entry_fee ? `Fee: ${t.entry_fee}. ` : ''}Set entry fee & payment details when creating the tournament.</p>
        </div>
        <button onClick={copyLink} className="px-3 py-2.5 bg-[#1C1C1E] border border-gray-700 rounded-xl text-xs font-bold flex items-center gap-1.5 shrink-0"><Share2 size={14} />{copied ? 'Copied' : 'Public link'}</button>
      </div>

      <div>
        <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Teams entered — pending payment ({pending.length})</h3>
        {pending.length === 0 ? <p className="text-center text-gray-600 py-6 text-sm">No pending entries.</p> :
          <div className="space-y-2">
            {pending.map((e) => (
              <div key={e.id} data-testid={`entry-pending-${e.id}`} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold truncate">{e.team_name}</p>
                  <p className="text-xs text-gray-500 truncate">{[e.manager, e.contact].filter(Boolean).join(' · ') || 'No contact'}</p>
                  {e.notes && <p className="text-[11px] text-gray-600 truncate">{e.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`confirm-entry-${e.id}`} onClick={() => confirmEntry(e.id)} className="bg-[#00D26A] text-black font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-1"><Check size={14} />Confirm payment</button>
                  <button onClick={() => removeEntry(e.id)} className="text-gray-600 hover:text-[#FF3B30]"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>}
      </div>

      <div>
        <h3 className="text-xs font-black uppercase tracking-widest text-[#00D26A] mb-2">Paid — in the tournament ({paid.length})</h3>
        {paid.length === 0 ? <p className="text-center text-gray-600 py-6 text-sm">No confirmed entries yet.</p> :
          <div className="space-y-2">
            {paid.map((e) => (
              <div key={e.id} data-testid={`entry-paid-${e.id}`} className="bg-[#00D26A]/5 border border-[#00D26A]/30 rounded-xl px-4 py-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold truncate flex items-center gap-2"><Check size={14} className="text-[#00D26A]" />{e.team_name}</p>
                  <p className="text-xs text-gray-500 truncate">{[e.manager, e.contact].filter(Boolean).join(' · ')}</p>
                </div>
                <button onClick={() => removeEntry(e.id)} className="text-gray-600 hover:text-[#FF3B30] shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}
